import { Injectable } from '@angular/core';
import { AngularFireDatabase } from 'angularfire2/database';
import 'rxjs/add/operator/take';
import { Observable } from 'rxjs/Observable';
import { map, switchMap } from 'rxjs/operators';
import { AngularFirestore } from 'angularfire2/firestore';
import { IPoll } from '../models/poll.model';
import { isEmpty } from 'lodash';
import { UtilitiesProvider } from './utilities';
import { ENV } from '@app/env';
import * as firebase from 'firebase';

@Injectable()
export class PollData {

  constructor(
    private afdb: AngularFireDatabase,
    private afStore: AngularFirestore,
    private utilities: UtilitiesProvider
  ) { }

  // ******************* GENERAL POLL *******************

  /**
   * 1. Search an active opinometer
   * @param clientId
   */
  public getClientActivePoll(clientId: string) {
    return this.afdb.object(`/Clients/${clientId}/Interactivity/Poll`).valueChanges()
  }

  public userClientHasViewPoll(pollId: string, userId: string) {
    return this.afStore.collection(ENV.client).doc('poll-views').collection(pollId, ref => {
      return ref.where('userId', '==', userId);
    }).valueChanges()
      .pipe(map((views) => {
        return !isEmpty(views);
      }))
  }

  public saveClientPollView(pollId: string,
    userId: string): Observable<any> {
    let id = this.afdb.createPushId();
    return Observable.fromPromise(this.afStore.collection(ENV.client).doc('poll-views').collection(pollId)
      .doc(<string>id).set({
        id: id,
        create_at: firebase.firestore.FieldValue.serverTimestamp(),
        pollId: pollId,
        userId: userId
      }))
  };

  public saveClientPollStatistic(pollId: string) {
    return Observable.fromPromise(this.afdb.object(`/Clients/${ENV.client}/stats/polls/${pollId}`)
      .query.ref.transaction((pollStats) => {
        if (pollStats) {
          return pollStats + 1;
        } else {
          return 1
        }
      }));
  }

  public saveClientPollStatisticAndView(pollId: string, userId: string) {
    return this.saveClientPollStatistic(pollId)
      .pipe(switchMap(() => {
        return this.saveClientPollView(pollId, userId)
      }))
  }

  public canShowActivePollToUser(clientId: string, pollId: string, userId: string) {
    return this.afStore.collection(clientId).doc('poll-views')
      .collection(`${pollId}`, ref => {
        return ref.where('userId', '==', userId).where('pollId', '==', pollId)
      })
      .valueChanges()
      .pipe(
        map(userhasRecord => {
          this.utilities.consoleDecorator('userhasRecord', userhasRecord);
          return (!isEmpty(userhasRecord)) ? { show: false } : { show: true, pollId: pollId };
        }));
  }

  public getPollByClientId(clientId: string, pollId: string): Observable<IPoll> {
    return this.afdb.object<IPoll>(`/Clients/${clientId}/Polls/${pollId}`).valueChanges()
  }

  public getUserAnswer(clientId: string, pollId: string, userId: string) {
    return this.afStore.collection(clientId).doc('poll-answers')
      .collection(`${pollId}`, ref => {
        return ref.where('userId', '==', userId).where('pollId', '==', pollId)
      })
      .valueChanges()
      .map((data) => {
        return (isEmpty(data)) ? data : data[0];
      });
  }

  public savePollAnswer(clientId: string, pollId: string, userId: string, answers: any) {
    return this.afStore.collection(clientId).doc('poll-answers')
      .collection(`${pollId}`, ref => {
        return ref.where('pollId', '==', pollId).where('userId', '==', userId)
      }).valueChanges().pipe(
        (map((userAnswer: any) => {
          this.utilities.consoleDecorator('userAnswer', userAnswer);
          if (!isEmpty(userAnswer)) {
            return Observable.fromPromise(this.afStore.collection(clientId).doc('poll')
              .collection(`${pollId}`).doc(<string>userAnswer[0].id).set({
                "id": userAnswer[0].id,
                "pollId": pollId,
                "userId": userId,
                "answers": answers
              }));
          } else {
            let id = this.afdb.createPushId();
            return Observable.fromPromise(this.afStore.collection(clientId).doc('poll-answers')
              .collection(`${pollId}`).doc(<string>id).set({
                "id": id,
                "pollId": pollId,
                "userId": userId,
                "answers": answers
              }));
          }

        }))
      )
  }

  // ******************* EVENT POLL *******************
  /**
   * 1. Search an active opinometer
   * @param clientId
   * @param eventId
   */
  public getEventActivePoll(clientId: string, eventId: string) {
    return this.afdb.object(`/Clients/${clientId}/EventConfig/${eventId}/interactivity/Poll`).valueChanges()
  }


  public canShowActiveEventPollToUser(clientId: string, eventId: string, pollId: string, userId: string) {
    return this.afStore.collection(clientId).doc('poll-event-views').collection('event').doc(eventId).collection('polls').doc(pollId).collection('views', ref => {
      return ref.where('userId', '==', userId).where('pollId', '==', pollId)
    })
      .valueChanges()
      .pipe(
        map(userhasRecord => {
          return (!isEmpty(userhasRecord)) ? { show: false } : { show: true, pollId: pollId };
        }));
  }

  public getUserEventAnswer(clientId: string, eventId: string, pollId: string, userId: string) {
    return this.afStore.collection(clientId).doc('poll-events')
      .collection(`${pollId}`).doc('event')
      .collection(`${eventId}`, ref => {
        return ref.where('userId', '==', userId).where('pollId', '==', pollId)
      })
      .valueChanges()
      .map((data) => {
        return (isEmpty(data)) ? data : data[0];
      });
  }

  public saveEventPollView(eventId: string, pollId: string,
    userId: string): Observable<any> {
    let id = this.afdb.createPushId();
    return Observable.fromPromise(this.afStore.collection(ENV.client).doc('poll-event-views').collection('event').doc(eventId).collection('polls').doc(pollId).collection('views')
      .doc(<string>id).set({
        id: id,
        create_at: firebase.firestore.FieldValue.serverTimestamp(),
        pollId: pollId,
        userId: userId,
        eventId: eventId
      }))
  };

  public saveEventPollStatistic(eventId: string, pollId: string) {
    return Observable.fromPromise(this.afdb.object(`/Clients/${ENV.client}/Events/${eventId}/stats/polls/${pollId}`)
      .query.ref.transaction((messageStats) => {
        if (messageStats) {
          return messageStats + 1;
        } else {
          return 1
        }
      }));
  }

  public saveEventPollStatisticAndView(eventId: string, pollId: string, userId: string) {
    return this.saveEventPollStatistic(eventId, pollId)
      .pipe(switchMap(() => {
        return this.saveEventPollView(eventId, pollId, userId)
      }))
  }


  public saveEventPollAnswer(clientId: string, eventId: string, pollId: string, userId: string, answers: any) {
    return this.afStore.collection(clientId).doc('poll-events').collection(`${pollId}`).doc('event')
      .collection(`${eventId}`, ref => {
        return ref.where('pollId', '==', pollId).where('userId', '==', userId)
      }).valueChanges().pipe(
        (map((userAnswer: any) => {
          if (!isEmpty(userAnswer)) {
            return Observable.fromPromise(this.afStore.collection(clientId).doc('poll-events').collection(`${pollId}`).doc('event')
              .collection(`${eventId}`).doc(<string>userAnswer[0].id).set({
                "id": userAnswer[0].id,
                "pollId": pollId,
                "userId": userId,
                "answers": answers
              }));
          } else {
            let id = this.afdb.createPushId();
            return Observable.fromPromise(this.afStore.collection(clientId).doc('poll-events').collection(`${pollId}`).doc('event')
              .collection(`${eventId}`).doc(<string>id).set({
                "id": id,
                "pollId": pollId,
                "userId": userId,
                "answers": answers,
              }));
          }

        }))
      )
  }

}


// WEBPACK FOOTER //
// ./src/providers/poll.ts